


































































import {Component, Prop, Vue} from "vue-property-decorator";
import PxDropdown from "@/views/components/input/formFields/PxDropdown.vue";
import PxNumberInput from "@/views/components/input/formFields/PxNumberInput.vue";
import ProbabilisticChart from "@/modules/lcca/views/components/ProbablisticChart.vue";
import PxTextArea from "@/views/components/input/formFields/PxTextArea.vue";
import PxTextEntryBox from "@/views/components/input/formFields/PxTextEntryBox.vue";
import QuickHelp from "@/views/components/input/QuickHelp.vue";
import {UnitSystem} from "@/interfaces/units";
import {lccaStore, perRoadStore} from "@/store/store";
import {getPeakHorizontalStrain} from "@/modules/perRoad/lib/calculations/horizontalStrainDistribution";
import {
  meanThicknessOfTopLayer,
  setTopLayerThicknessAndMakeACopy,
  simulateAndGetTotalError
} from "@/modules/perRoad/lib/simulation/optimization";

@Component({
  components: {
    QuickHelp,
    PxTextEntryBox,
    PxTextArea,
    ProbabilisticChart,
    PxDropdown,
    PxNumberInput,
  },
})
export default class Optimization extends Vue {
  @Prop() distributionPercentiles!: number[];

  @Prop() distribution_ustrain!: number[];

  getPeakHorizontalStrain = getPeakHorizontalStrain;

  currentPage = 1;

  plotKey = 0;


  get scenarioImperial() {
    return perRoadStore.currentImperial;
  }


  updatePlotKey() {
    this.plotKey += 1;
  }

  sync() {
    this.updatePlotKey();
    this.$emit("sync");
  }

  unitSystemIsImperial() {
    return lccaStore.displayUnits === UnitSystem.Imperial;

  }

  setOptimizedThicknessAndCloseModal() {
    this.scenarioImperial.layers[0].thickness_in.mean = this.solutionThickness;
    perRoadStore.setCurrentScenario(this.scenarioImperial);
    this.$bvModal.hide("optimize-modal");
  }

  /**
   * Algorithm configuration
   */

  currentlyRunning = false;

  // Bisection variables
  startLow = 0.1; // Inches, error is negative
  startHigh = 100; // Inches, error is positive

  // Tunable parameters
  drawsPerRun = 250;
  iterationLimit = 10;
  thicknessTol_in = 0.5; //inches
  errorTol = 10; // percent, cumulative, across all five points
  initialError: null | number = null;

  meanThicknessHistory: [number, number][] = []; // [[Mean thickness, error]]
  paramHistory: [number, number, number][] = []; // [[a,b,c]]

  /**
   * Instrumentation
   */
  get allowAcceptSolution() {
    return !this.currentlyRunning && this.solutionThickness !== null;
  }


  get solutionThickness(): number | null {
    if (this.meanThicknessHistory.length === 0) {
      return null;
    }

    let latestThickness = 100;

    this.meanThicknessHistory.forEach(iterResult => {
      const t = iterResult[0];
      const e = iterResult[1];

      if (t < latestThickness && e > 0) {
        latestThickness = t;
      }
    });

    /**
     * Round result to nearest half-inch
     */

    const tTemp = latestThickness * 10;
    latestThickness = (Math.ceil(tTemp / 5) * 5) / 10;

    return latestThickness;
  }

  firstRow = {
    lowerBound: this.startLow.valueOf(),
    thickness_in: meanThicknessOfTopLayer(this.scenarioImperial).valueOf(),
    upperBound: this.startHigh.valueOf(),
    error: this.initialError
  };

  get items() {
    let rows = [this.firstRow];
    this.meanThicknessHistory.forEach((x, i) => {
      rows.push({
        lowerBound: this.paramHistory[i][0],
        thickness_in: x[0],
        upperBound: this.paramHistory[i][1],
        error: x[1]
      });
    });
    return rows;
  }

  /**
   * Algorithm implementation
   */

  iterations = 0;
  currentIterationCompletedRuns = 0;

  setCurrentIterationCompletedRuns(n: number) {
    this.currentIterationCompletedRuns = n;
  }

  statusText: "Idle" | "Setting upper and lower bounds" | "Optimizing" = "Idle";

  async optimize() {
    this.currentlyRunning = true;
    this.iterations = 0;

    /**
     * Set high, low values
     *
     * If current error is negative: startLow is current thickness -> Set startHigh to a large value
     *
     * If current error is positive: startHigh is current thickness -> Set startLow to a low value
     */

    this.statusText = "Setting upper and lower bounds";

    const initialError = await simulateAndGetTotalError(this.drawsPerRun, this.scenarioImperial, this.distributionPercentiles, this.distribution_ustrain, this.setCurrentIterationCompletedRuns);

    console.log("Initial Error", initialError)

    if (initialError < -20) {
      this.startLow = meanThicknessOfTopLayer(this.scenarioImperial);
      // this.startHigh = meanThicknessOfTopLayer(this.scenarioImperial) * 3;
    } else if (initialError > 20) {
      // this.startLow = meanThicknessOfTopLayer(this.scenarioImperial) / 3;
      this.startHigh = meanThicknessOfTopLayer(this.scenarioImperial);
    }

    this.meanThicknessHistory.push([meanThicknessOfTopLayer(this.scenarioImperial), initialError]);
    this.paramHistory.push([this.startLow, this.startHigh, meanThicknessOfTopLayer(this.scenarioImperial)]);


    /**
     * Perform bisection
     */
    let a = this.startLow;
    let fa = await simulateAndGetTotalError(this.drawsPerRun, setTopLayerThicknessAndMakeACopy(a, this.scenarioImperial), this.distributionPercentiles, this.distribution_ustrain, this.setCurrentIterationCompletedRuns);

    let b = this.startHigh;
    // let fb = await simulateAndGetTotalError(this.drawsPerRun, setTopLayerThicknessAndMakeACopy(b, this.scenarioImperial), this.distributionPercentiles, this.distribution_ustrain);


    this.statusText = "Optimizing";
    while (this.iterations < this.iterationLimit) {
      const c = (a + b) / 2;

      this.paramHistory.push([a, b, c]);

      const midPointScenario = setTopLayerThicknessAndMakeACopy(c, this.scenarioImperial);

      const fc = await simulateAndGetTotalError(this.drawsPerRun, midPointScenario, this.distributionPercentiles, this.distribution_ustrain, this.setCurrentIterationCompletedRuns);

      /**
       * Check stopping criteria
       *
       * If error is positive and difference between a and b is less than tolerance, exit.
       *
       */
      console.log(`Bisect result =>  ${fc}, ${Math.abs(fc)}%, ${b - a} : ${this.thicknessTol_in} in`);
      if (Math.abs(fc) < this.errorTol && fc > 0 && (b - a) < this.thicknessTol_in) {
        console.log("Bisect exit condition reached");
        this.meanThicknessHistory.push([meanThicknessOfTopLayer(midPointScenario), fc]);
        break;
      }

      if (Math.abs(fc) < this.errorTol && fc < 0 && (b - a) < this.thicknessTol_in) {
        console.log("Bisect exit condition reached but error is < 0");
        // this.meanThicknessHistory.push([meanThicknessOfTopLayer(midPointScenario), fc]);
        // break;
      }

      /**
       * Set new a, b values
       */

          // @ts-ignore: Property 'at' does not exist on type [number number][] -> but it does??
      const lastErrorValue = this.meanThicknessHistory.at(-1)[1];
      console.log(fc, Math.sign(fc), lastErrorValue, Math.sign(lastErrorValue));

      if (Math.sign(fc) === Math.sign(fa)) {
        a = c;
        fa = fc;
      } else {
        b = c;
        // fb = fc;
      }

      this.meanThicknessHistory.push([meanThicknessOfTopLayer(midPointScenario), fc]);
      this.iterations += 1;
    }

    this.statusText = "Idle";
    this.currentlyRunning = false;
  }
}
